import React, { useCallback, useEffect, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../config/firebaseConfig";
import {
  hasPermission,
  PERMISSIONS,
} from "../utils/roleAccess";

const CLASS_PROGRESSION = {
  Nursery: "Nursery_A",
  Nursery_A: "Nursery_B",
  Nursery_B: "LKG",
  LKG: "UKG",
  UKG: "Class 1",
  "Class 1": "Class 2",
  "Class 2": "Class 3",
  "Class 3": "Class 4",
  "Class 4": "Class 5",
  "Class 5": "Class 6",
  "Class 6": "Class 7",
  "Class 7": "Class 8",
  "Class 8": null,
};

const CLASS_ROLLBACK = {
  Nursery_A: "Nursery",
  Nursery_B: "Nursery_A",
  LKG: "Nursery_B",
  UKG: "LKG",
  "Class 1": "UKG",
  "Class 2": "Class 1",
  "Class 3": "Class 2",
  "Class 4": "Class 3",
  "Class 5": "Class 4",
  "Class 6": "Class 5",
  "Class 7": "Class 6",
  "Class 8": "Class 7",
  Nursery: null,
};

const CLASS_OPTIONS = [
  "Nursery",
  "Nursery_A",
  "Nursery_B",
  "LKG",
  "UKG",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
];

const NORMALIZE_MAP = {
  // Nursery variants
  nc: "Nursery",
  nur: "Nursery",
  nursery: "Nursery",
  nurseries: "Nursery",
  nurserya: "Nursery_A",
  nurseryb: "Nursery_B",
  // LKG variants
  "l.k.g": "LKG",
  lkg: "LKG",
  "l k g": "LKG",
  // UKG is already UKG
  // Roman numerals to class
  i: "Class 1",
  ii: "Class 2",
  iii: "Class 3",
  iv: "Class 4",
  v: "Class 5",
  vi: "Class 6",
  vii: "Class 7",
};

function normalizeClassName(raw) {
  if (!raw && raw !== 0) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const key = s.toLowerCase().replace(/\./g, "").replace(/\s+/g, "");
  if (NORMALIZE_MAP[key]) return NORMALIZE_MAP[key];
  // If already one of CLASS_OPTIONS (case-insensitive match), return canonical
  for (const opt of CLASS_OPTIONS) {
    if (opt.toLowerCase() === s.toLowerCase()) return opt;
  }
  return s;
}

function getReadableErrorMessage(error) {
  return error?.message || "Unexpected error. Please try again.";
}

export default function ClassPromotionManager() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [students2025, setStudents2025] = useState([]);
  const [selectedForPromotion, setSelectedForPromotion] = useState(new Set());
  // Editable class mapping so admin can change Nursery/LKG/UKG progression before promoting
  const [classMapping, setClassMapping] = useState(() => ({
    ...CLASS_PROGRESSION,
    Nursery: "LKG",
    Nursery_A: "LKG",
    Nursery_B: "LKG",
  }));
  const [normalizePreview, setNormalizePreview] = useState([]);
  const [normalizingLoading, setNormalizingLoading] = useState(false);
  const [applyingNormalization, setApplyingNormalization] = useState(false);
  const [normalizationProgress, setNormalizationProgress] = useState({
    total: 0,
    done: 0,
  });
  const [normalizationSummary, setNormalizationSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promotionProgress, setPromotionProgress] = useState({
    total: 0,
    done: 0,
  });
  const [promotionSummary, setPromotionSummary] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedForRollback, setSelectedForRollback] = useState(new Set());
  const [rolling, setRolling] = useState(false);
  const [rollbackProgress, setRollbackProgress] = useState({
    total: 0,
    done: 0,
  });
  const [rollbackSummary, setRollbackSummary] = useState(null);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) return;
      try {
        if (!currentUser) {
          setUser(null);
          setCanManage(false);
          return;
        }
        setUser(currentUser);
        const userSnapshot = await getDocs(
          query(
            collection(db, "users"),
            where("__name__", "==", currentUser.uid),
          ),
        );
        const userData = userSnapshot.empty
          ? null
          : userSnapshot.docs[0].data();
        setCanManage(
          Boolean(
            userData && hasPermission(userData, PERMISSIONS.ADMIN_DASHBOARD),
          ),
        );
      } finally {
        if (active) setAuthLoading(false);
      }
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const handleLoadStudents = useCallback(async () => {
    setLoading(true);
    setStudents2025([]);
    setPromotionSummary(null);
    try {
      const allStudentsSnapshot = await getDocs(collection(db, "students"));
      const filtered = allStudentsSnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .map((student) => ({
          ...student,
          className: normalizeClassName(student.className),
        }))
        .filter((student) => {
          const uid = String(student.uid || "").trim();
          const activeqr = student.activeqr === true;
          return uid.startsWith("25") && activeqr;
        });
      setStudents2025(filtered);
      setSelectedForPromotion(new Set());
      setSnackbarState({
        open: true,
        severity: "info",
        message: `Found ${filtered.length} students with active QR codes (UID: 25XXXX).`,
      });
    } catch (error) {
      console.error("Error loading students:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTogglePromote = useCallback((studentId) => {
    setSelectedForPromotion((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  }, []);

  const handleSelectAllPromote = useCallback(() => {
    setSelectedForPromotion((prev) => {
      if (prev.size === students2025.length) return new Set();
      return new Set(students2025.map((s) => s.id));
    });
  }, [students2025]);

  const handleClassMappingChange = useCallback((key, value) => {
    setClassMapping((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePreviewNormalization = useCallback(async () => {
    setNormalizingLoading(true);
    setNormalizePreview([]);
    try {
      const snap = await getDocs(collection(db, "students"));
      const candidates = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const diffs = candidates
        .filter((s) => s.activeqr === true || s.activeqr === "true")
        .map((s) => {
          const oldClass = String(s.className || "").trim();
          const newClass = normalizeClassName(oldClass);
          return {
            id: s.id,
            uid: s.uid || "N/A",
            name: s.name || "N/A",
            oldClass,
            newClass,
          };
        })
        .filter((x) => x.oldClass !== x.newClass);

      setNormalizePreview(diffs);
      setSnackbarState({
        open: true,
        severity: "info",
        message: `Found ${diffs.length} students with class names to normalize.`,
      });
    } catch (error) {
      console.error("Normalization preview error:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setNormalizingLoading(false);
    }
  }, []);

  const handleApplyNormalization = useCallback(async () => {
    if (!normalizePreview.length) {
      setSnackbarState({
        open: true,
        severity: "warning",
        message: "No normalization changes to apply.",
      });
      return;
    }

    const confirmPrompt = window.confirm(
      `Apply normalization to ${normalizePreview.length} students? This will update their className fields.`,
    );
    if (!confirmPrompt) return;

    setApplyingNormalization(true);
    setNormalizationSummary(null);
    setNormalizationProgress({ total: normalizePreview.length, done: 0 });

    let applied = 0,
      errors = 0;
    const errorDetails = [];
    const updateNormalizationDone = (done) => {
      setNormalizationProgress((prev) => ({
        ...prev,
        done,
      }));
    };

    try {
      const batchSizeLimit = 400;
      let batchCount = 0;
      let batch = writeBatch(db);

      for (let i = 0; i < normalizePreview.length; i++) {
        const row = normalizePreview[i];
        try {
          batch.update(doc(db, "students", row.id), {
            className: row.newClass,
            updatedAt: serverTimestamp(),
          });
          batchCount++;
          applied++;

          if (batchCount >= batchSizeLimit) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }

          updateNormalizationDone(applied + errors);
        } catch (err) {
          errors++;
          errorDetails.push(`${row.name || row.id}: ${err.message}`);
          updateNormalizationDone(applied + errors);
        }
      }

      if (batchCount > 0) await batch.commit();

      setNormalizationSummary({
        applied,
        errors,
        errorDetails: errorDetails.slice(0, 10),
      });
      setSnackbarState({
        open: true,
        severity: errors > 0 ? "warning" : "success",
        message: `Applied normalization to ${applied} students.`,
      });
      setNormalizePreview([]);
    } catch (err) {
      console.error("Apply normalization error:", err);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(err),
      });
    } finally {
      setApplyingNormalization(false);
    }
  }, [normalizePreview]);

  const handlePromoteStudents = useCallback(async () => {
    const targets =
      selectedForPromotion && selectedForPromotion.size > 0
        ? students2025.filter((s) => selectedForPromotion.has(s.id))
        : students2025;

    if (!targets.length) {
      setSnackbarState({
        open: true,
        severity: "warning",
        message: "No students to promote. Load students first.",
      });
      return;
    }

    const confirmPrompt = window.confirm(
      `Promote ${targets.length} students to the next class?`,
    );
    if (!confirmPrompt) return;

    setPromoting(true);
    setPromotionSummary(null);
    setPromotionProgress({ total: targets.length, done: 0 });

    let promoted = 0,
      skipped = 0,
      errors = 0;
    const errorDetails = [];
    const updatePromotionDone = (done) => {
      setPromotionProgress((prev) => ({
        ...prev,
        done,
      }));
    };

    try {
      const batchSizeLimit = 400;
      let batchCount = 0;
      let batch = writeBatch(db);

      for (let index = 0; index < targets.length; index++) {
        const student = targets[index];
        const currentClass = String(student.className || "").trim();
        const nextClass = classMapping[currentClass];

        if (!nextClass) {
          errorDetails.push(
            `${student.name || student.uid}: Already in final class`,
          );
          skipped++;
          updatePromotionDone(promoted + skipped + errors);
          continue;
        }

        try {
          batch.update(doc(db, "students", student.id), {
            className: nextClass,
            updatedAt: serverTimestamp(),
          });
          batchCount++;
          promoted++;

          if (batchCount >= batchSizeLimit) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
          updatePromotionDone(promoted + skipped + errors);
        } catch (error) {
          errorDetails.push(`${student.name || student.uid}: ${error.message}`);
          errors++;
          updatePromotionDone(promoted + skipped + errors);
        }
      }

      if (batchCount > 0) await batch.commit();

      setPromotionSummary({
        promoted,
        skipped,
        errors,
        errorDetails: errorDetails.slice(0, 10),
      });

      const message =
        errors === 0
          ? `Promoted ${promoted} students successfully.`
          : `Promoted ${promoted} students with ${errors} errors.`;

      setSnackbarState({
        open: true,
        severity: errors > 0 ? "warning" : "success",
        message,
      });
    } catch (error) {
      console.error("Class promotion error:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setPromoting(false);
    }
  }, [classMapping, selectedForPromotion, students2025]);

  const handleLoadAllStudents = useCallback(async () => {
    // Load only rollback candidates: UID starts with "25" and activeqr === true
    setLoading(true);
    setAllStudents([]);
    setSelectedForRollback(new Set());
    setRollbackSummary(null);
    try {
      const allStudentsSnapshot = await getDocs(collection(db, "students"));
      const students = allStudentsSnapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .map((student) => ({
          ...student,
          className: normalizeClassName(student.className),
        }))
        .filter((student) => {
          const uid = String(student.uid || "").trim();
          const activeqr = student.activeqr === true;
          return uid.startsWith("25") && activeqr;
        });

      setAllStudents(students);
      setSnackbarState({
        open: true,
        severity: "info",
        message: `Loaded ${students.length} rollback candidates (UID: 25XXXX, activeqr=true).`,
      });
    } catch (error) {
      console.error("Error loading rollback candidates:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggleStudent = useCallback((studentId) => {
    setSelectedForRollback((prev) => {
      const newSet = new Set(prev);
      newSet.has(studentId) ? newSet.delete(studentId) : newSet.add(studentId);
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedForRollback(
      selectedForRollback.size === allStudents.length
        ? new Set()
        : new Set(allStudents.map((s) => s.id)),
    );
  }, [allStudents, selectedForRollback]);

  const handleRollbackStudents = useCallback(async () => {
    if (selectedForRollback.size === 0) {
      setSnackbarState({
        open: true,
        severity: "warning",
        message: "No students selected.",
      });
      return;
    }

    const confirmPrompt = window.confirm(
      `Rollback ${selectedForRollback.size} students to their previous class?`,
    );
    if (!confirmPrompt) return;

    setRolling(true);
    setRollbackSummary(null);
    setRollbackProgress({ total: selectedForRollback.size, done: 0 });

    let rolled = 0,
      skipped = 0,
      errors = 0;
    const errorDetails = [];
    const updateRollbackDone = (done) => {
      setRollbackProgress((prev) => ({
        ...prev,
        done,
      }));
    };

    try {
      const batchSizeLimit = 400;
      let batchCount = 0;
      let batch = writeBatch(db);

      for (const studentId of selectedForRollback) {
        const student = allStudents.find((s) => s.id === studentId);
        if (!student) {
          skipped++;
          continue;
        }

        const currentClass = String(student.className || "").trim();
        const previousClass = CLASS_ROLLBACK[currentClass];

        if (!previousClass) {
          errorDetails.push(
            `${student.name || student.uid}: Cannot rollback from ${currentClass}`,
          );
          skipped++;
          updateRollbackDone(rolled + skipped + errors);
          continue;
        }

        try {
          batch.update(doc(db, "students", student.id), {
            className: previousClass,
            updatedAt: serverTimestamp(),
          });
          batchCount++;
          rolled++;

          if (batchCount >= batchSizeLimit) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }
          updateRollbackDone(rolled + skipped + errors);
        } catch (error) {
          errorDetails.push(`${student.name || student.uid}: ${error.message}`);
          errors++;
          updateRollbackDone(rolled + skipped + errors);
        }
      }

      if (batchCount > 0) await batch.commit();

      setRollbackSummary({
        rolled,
        skipped,
        errors,
        errorDetails: errorDetails.slice(0, 10),
      });

      const message =
        errors === 0
          ? `Rolled back ${rolled} students successfully.`
          : `Rolled back ${rolled} students with ${errors} errors.`;

      setSnackbarState({
        open: true,
        severity: errors > 0 ? "warning" : "success",
        message,
      });

      setSelectedForRollback(new Set());
    } catch (error) {
      console.error("Rollback error:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setRolling(false);
    }
  }, [selectedForRollback, allStudents]);

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "grid",
          placeItems: "center",
          bgcolor: "grey.50",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!user || !canManage) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "grid",
          placeItems: "center",
          bgcolor: "grey.50",
        }}
      >
        <Card
          sx={{
            maxWidth: 520,
            width: "100%",
            borderRadius: 4,
            boxShadow: "none",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h5" fontWeight={800}>
              Access required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You need admin permissions to access this feature.
            </Typography>
            <Button
              onClick={() => navigate("/login")}
              variant="contained"
              sx={{ mt: 3, borderRadius: 999 }}
            >
              Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, bgcolor: "grey.50", minHeight: "calc(100vh - 120px)" }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack spacing={0.5}>
              <Typography variant="h4" fontWeight={900}>
                Class Promotion & Rollback Manager
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage student class promotions and rollbacks.
              </Typography>
            </Stack>
            <Button
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate("/admin-dashboard")}
              sx={{ borderRadius: 999 }}
            >
              Back
            </Button>
          </Stack>

          <Paper sx={{ borderRadius: 4, boxShadow: "none" }}>
            <Tabs
              value={tabValue}
              onChange={(_, val) => setTabValue(val)}
              sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                px: { xs: 2, md: 4 },
              }}
            >
              <Tab label="Promote Students" />
              <Tab label="Rollback Classes" />
              <Tab label="Normalize Classes" />
            </Tabs>

            {tabValue === 0 && (
              <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={3}>
                  <Card sx={{ borderRadius: 2, bgcolor: "grey.50" }}>
                    <CardContent>
                      <Stack spacing={2.5}>
                        <Typography variant="h6" fontWeight={800}>
                          Load Students
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fetch students with UID starting with "25" and
                          activeqr = true. Edit mapping below (Nursery/LKG/UKG)
                          before promoting.
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={
                            loading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <PublishRoundedIcon />
                            )
                          }
                          disabled={loading || promoting}
                          onClick={handleLoadStudents}
                          sx={{ borderRadius: 999, alignSelf: "flex-start" }}
                        >
                          {loading
                            ? "Loading..."
                            : `Load Students (${students2025.length} found)`}
                        </Button>

                        <Card sx={{ mt: 2, p: 2, bgcolor: "grey.50" }}>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              Class Mapping (editable)
                            </Typography>
                          </Stack>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            sx={{ mt: 2 }}
                          >
                            <Stack>
                              <Typography variant="caption">
                                Nursery →
                              </Typography>
                              <Select
                                value={classMapping["Nursery"] || ""}
                                onChange={(e) =>
                                  handleClassMappingChange(
                                    "Nursery",
                                    e.target.value,
                                  )
                                }
                                size="small"
                                sx={{ minWidth: 140 }}
                              >
                                {CLASS_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                            <Stack>
                              <Typography variant="caption">
                                Nursery_A →
                              </Typography>
                              <Select
                                value={classMapping["Nursery_A"] || ""}
                                onChange={(e) =>
                                  handleClassMappingChange(
                                    "Nursery_A",
                                    e.target.value,
                                  )
                                }
                                size="small"
                                sx={{ minWidth: 140 }}
                              >
                                {CLASS_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                            <Stack>
                              <Typography variant="caption">
                                Nursery_B →
                              </Typography>
                              <Select
                                value={classMapping["Nursery_B"] || ""}
                                onChange={(e) =>
                                  handleClassMappingChange(
                                    "Nursery_B",
                                    e.target.value,
                                  )
                                }
                                size="small"
                                sx={{ minWidth: 140 }}
                              >
                                {CLASS_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                            <Stack>
                              <Typography variant="caption">LKG →</Typography>
                              <Select
                                value={classMapping["LKG"] || ""}
                                onChange={(e) =>
                                  handleClassMappingChange(
                                    "LKG",
                                    e.target.value,
                                  )
                                }
                                size="small"
                                sx={{ minWidth: 120 }}
                              >
                                {CLASS_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                            <Stack>
                              <Typography variant="caption">UKG →</Typography>
                              <Select
                                value={classMapping["UKG"] || ""}
                                onChange={(e) =>
                                  handleClassMappingChange(
                                    "UKG",
                                    e.target.value,
                                  )
                                }
                                size="small"
                                sx={{ minWidth: 120 }}
                              >
                                {CLASS_OPTIONS.map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Stack>
                          </Stack>
                        </Card>
                      </Stack>
                    </CardContent>
                  </Card>

                  {students2025.length > 0 && (
                    <Card sx={{ borderRadius: 2, bgcolor: "grey.50" }}>
                      <CardContent>
                        <Stack spacing={2.5}>
                          <Typography variant="h6" fontWeight={800}>
                            Promote to Next Class
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Promote {students2025.length} students to their next
                            class level.
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Button
                              variant="contained"
                              color="success"
                              disabled={promoting || students2025.length === 0}
                              onClick={handlePromoteStudents}
                              sx={{ borderRadius: 999 }}
                            >
                              {promoting
                                ? `Promoting (${promotionProgress.done}/${promotionProgress.total})...`
                                : `Promote ${students2025.length} Students`}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={handleSelectAllPromote}
                              sx={{ borderRadius: 999 }}
                            >
                              {selectedForPromotion.size ===
                                students2025.length && students2025.length > 0
                                ? "Deselect All"
                                : "Select All"}
                            </Button>
                          </Box>
                          <TableContainer
                            sx={{
                              mt: 2,
                              maxHeight: 340,
                              overflowY: "auto",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                            }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ bgcolor: "grey.100" }}>
                                  <TableCell width={40} />
                                  <TableCell fontWeight={700}>
                                    Student Name
                                  </TableCell>
                                  <TableCell fontWeight={700}>UID</TableCell>
                                  <TableCell align="center" fontWeight={700}>
                                    Current Class
                                  </TableCell>
                                  <TableCell align="center" fontWeight={700}>
                                    Next Class (preview)
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {students2025.map((student) => {
                                  const currentClass = String(
                                    student.className || "",
                                  ).trim();
                                  const nextClass = classMapping[currentClass];
                                  return (
                                    <TableRow
                                      key={student.id}
                                      sx={{
                                        bgcolor: selectedForPromotion.has(
                                          student.id,
                                        )
                                          ? "action.selected"
                                          : "inherit",
                                      }}
                                    >
                                      <TableCell>
                                        <Checkbox
                                          checked={selectedForPromotion.has(
                                            student.id,
                                          )}
                                          onChange={() =>
                                            handleTogglePromote(student.id)
                                          }
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography
                                          variant="body2"
                                          fontWeight={500}
                                        >
                                          {student.name || "N/A"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {String(student.uid || "").trim()}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            bgcolor: "primary.light",
                                            borderRadius: 1,
                                            display: "inline-block",
                                          }}
                                        >
                                          {currentClass || "N/A"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        {nextClass ? (
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                              px: 1.5,
                                              py: 0.5,
                                              bgcolor: "success.light",
                                              borderRadius: 1,
                                              display: "inline-block",
                                            }}
                                          >
                                            {nextClass}
                                          </Typography>
                                        ) : (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            No mapping
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {promotionSummary && (
                            <Alert
                              severity={
                                promotionSummary.errors === 0
                                  ? "success"
                                  : "warning"
                              }
                            >
                              <Typography variant="body2" fontWeight={700}>
                                Promotion Summary
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                ✓ Promoted: {promotionSummary.promoted}
                                <br />⊘ Skipped: {promotionSummary.skipped}
                                <br />✗ Errors: {promotionSummary.errors}
                              </Typography>
                              {promotionSummary.errorDetails.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    maxHeight: 200,
                                    overflowY: "auto",
                                  }}
                                >
                                  {promotionSummary.errorDetails.map(
                                    (msg, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {msg}
                                      </Typography>
                                    ),
                                  )}
                                </Box>
                              )}
                            </Alert>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Box>
            )}

            {tabValue === 1 && (
              <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={3}>
                  <Card sx={{ borderRadius: 2, bgcolor: "grey.50" }}>
                    <CardContent>
                      <Stack spacing={2.5}>
                        <Typography variant="h6" fontWeight={800}>
                          Load Rollback Candidates
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          View students with UID starting with "25" and activeqr
                          = true (promoted QR holders).
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={
                            loading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <PublishRoundedIcon />
                            )
                          }
                          disabled={loading || rolling}
                          onClick={handleLoadAllStudents}
                          sx={{ borderRadius: 999, alignSelf: "flex-start" }}
                        >
                          {loading
                            ? "Loading..."
                            : `Load Candidates (${allStudents.length} found)`}
                        </Button>
                        {selectedForRollback.size > 0 && (
                          <Alert severity="info">
                            {selectedForRollback.size} students selected for
                            rollback.
                          </Alert>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>

                  {allStudents.length > 0 && (
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={2.5}>
                          <Typography variant="h6" fontWeight={800}>
                            Select & Rollback
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 2,
                              bgcolor: "grey.50",
                              borderRadius: 2,
                            }}
                          >
                            <Checkbox
                              checked={
                                selectedForRollback.size ===
                                  allStudents.length && allStudents.length > 0
                              }
                              indeterminate={
                                selectedForRollback.size > 0 &&
                                selectedForRollback.size < allStudents.length
                              }
                              onChange={handleSelectAll}
                            />
                            <Typography variant="body2" fontWeight={600}>
                              {selectedForRollback.size ===
                                allStudents.length && allStudents.length > 0
                                ? "Deselect All"
                                : "Select All"}
                            </Typography>
                          </Box>

                          <TableContainer
                            sx={{
                              maxHeight: 400,
                              overflowY: "auto",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                            }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow sx={{ bgcolor: "grey.100" }}>
                                  <TableCell width={40} />
                                  <TableCell fontWeight={700}>
                                    Student Name
                                  </TableCell>
                                  <TableCell align="center" fontWeight={700}>
                                    Current Class
                                  </TableCell>
                                  <TableCell align="center" fontWeight={700}>
                                    Previous Class
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {allStudents.map((student) => {
                                  const currentClass = String(
                                    student.className || "",
                                  ).trim();
                                  const previousClass =
                                    CLASS_ROLLBACK[currentClass];
                                  const canRollback =
                                    previousClass !== undefined;
                                  return (
                                    <TableRow
                                      key={student.id}
                                      sx={{
                                        bgcolor: selectedForRollback.has(
                                          student.id,
                                        )
                                          ? "action.selected"
                                          : "inherit",
                                        opacity: canRollback ? 1 : 0.6,
                                      }}
                                    >
                                      <TableCell>
                                        <Checkbox
                                          checked={selectedForRollback.has(
                                            student.id,
                                          )}
                                          onChange={() =>
                                            handleToggleStudent(student.id)
                                          }
                                          disabled={!canRollback}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography
                                          variant="body2"
                                          fontWeight={500}
                                        >
                                          {student.name || "N/A"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            bgcolor: "primary.light",
                                            borderRadius: 1,
                                            display: "inline-block",
                                          }}
                                        >
                                          {currentClass || "N/A"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        {canRollback ? (
                                          <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{
                                              px: 1.5,
                                              py: 0.5,
                                              bgcolor: "success.light",
                                              borderRadius: 1,
                                              display: "inline-block",
                                            }}
                                          >
                                            {previousClass || "N/A"}
                                          </Typography>
                                        ) : (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Cannot rollback
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {selectedForRollback.size > 0 && (
                    <Card sx={{ borderRadius: 2, bgcolor: "warning.light" }}>
                      <CardContent>
                        <Stack spacing={2.5}>
                          <Typography variant="h6" fontWeight={800}>
                            Confirm Rollback
                          </Typography>
                          <Typography variant="body2">
                            {selectedForRollback.size} student(s) selected.
                            Click below to rollback them to their previous
                            classes.
                          </Typography>
                          <Button
                            variant="contained"
                            color="warning"
                            startIcon={
                              rolling ? (
                                <CircularProgress size={18} />
                              ) : (
                                <UndoRoundedIcon />
                              )
                            }
                            disabled={rolling || selectedForRollback.size === 0}
                            onClick={handleRollbackStudents}
                            sx={{ borderRadius: 999, alignSelf: "flex-start" }}
                          >
                            {rolling
                              ? `Rolling back (${rollbackProgress.done}/${rollbackProgress.total})...`
                              : `Rollback ${selectedForRollback.size} Students`}
                          </Button>
                          {rollbackSummary && (
                            <Alert
                              severity={
                                rollbackSummary.errors === 0
                                  ? "success"
                                  : "warning"
                              }
                            >
                              <Typography variant="body2" fontWeight={700}>
                                Rollback Summary
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                ✓ Rolled Back: {rollbackSummary.rolled}
                                <br />⊘ Skipped: {rollbackSummary.skipped}
                                <br />✗ Errors: {rollbackSummary.errors}
                              </Typography>
                              {rollbackSummary.errorDetails.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    maxHeight: 200,
                                    overflowY: "auto",
                                  }}
                                >
                                  {rollbackSummary.errorDetails.map(
                                    (msg, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {msg}
                                      </Typography>
                                    ),
                                  )}
                                </Box>
                              )}
                            </Alert>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Box>
            )}

            {tabValue === 2 && (
              <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={3}>
                  <Card sx={{ borderRadius: 2, bgcolor: "grey.50" }}>
                    <CardContent>
                      <Stack spacing={2.5}>
                        <Typography variant="h6" fontWeight={800}>
                          Normalize Classes (Data Cleanup)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          This scans all students with `activeqr = true` and
                          standardizes their class names (e.g. converting "NC",
                          "nur", "L.K.G", "II" into canonical formats).
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={handlePreviewNormalization}
                          disabled={normalizingLoading || applyingNormalization}
                          sx={{ alignSelf: "flex-start", borderRadius: 999 }}
                        >
                          {normalizingLoading
                            ? "Scanning..."
                            : "Scan for Normalization"}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>

                  {normalizePreview.length > 0 && (
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={3}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="h6" fontWeight={800}>
                              Candidates for Normalization (
                              {normalizePreview.length})
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleApplyNormalization}
                              disabled={applyingNormalization}
                              sx={{ borderRadius: 999 }}
                            >
                              {applyingNormalization
                                ? `Applying (${normalizationProgress.done}/${normalizationProgress.total})...`
                                : "Apply Normalization"}
                            </Button>
                          </Box>

                          <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ maxHeight: 500 }}
                          >
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Name</TableCell>
                                  <TableCell>UID</TableCell>
                                  <TableCell align="center">
                                    Current Class
                                  </TableCell>
                                  <TableCell align="center">
                                    Normalized Class
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {normalizePreview.map((row) => (
                                  <TableRow key={row.id}>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                      >
                                        {row.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {row.uid}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "error.main",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row.oldClass || "(empty)"}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "success.main",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {row.newClass}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {normalizationSummary && (
                            <Alert
                              severity={
                                normalizationSummary.errors === 0
                                  ? "success"
                                  : "warning"
                              }
                            >
                              <Typography variant="body2" fontWeight={700}>
                                Normalization Summary
                              </Typography>
                              <Typography variant="body2">
                                ✓ Applied: {normalizationSummary.applied} <br />
                                ✗ Errors: {normalizationSummary.errors}
                              </Typography>
                              {normalizationSummary.errorDetails.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    maxHeight: 150,
                                    overflowY: "auto",
                                  }}
                                >
                                  {normalizationSummary.errorDetails.map(
                                    (e, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="caption"
                                        display="block"
                                      >
                                        {e}
                                      </Typography>
                                    ),
                                  )}
                                </Box>
                              )}
                            </Alert>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </Box>
            )}
          </Paper>
        </Stack>
      </Container>

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason !== "clickaway")
            setSnackbarState((s) => ({ ...s, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbarState.severity} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
